/**
 * Style Transformer Utility
 *
 * Transforms content_json by applying template block styles to TipTap nodes.
 * This bridges the gap between template styling (defined in Template Builder)
 * and the TipTap editor display.
 *
 * Architecture:
 * 1. Extract styles from template blocks (attrs.style or legacy props)
 * 2. Build a lookup map by blockId
 * 3. Transform content_json nodes, applying styles where blockIds match
 *
 * @see /AI-DOCS/frontend-formatting-implementation.md for full design documentation
 */

import type { RTEDocument, RTENode, RTEMark } from './rteConverter';
import type { TemplateBlock, CSSProperties, BaseBlockProps } from './types';

/**
 * Style properties that TipTap handles as node-level attributes.
 * These go directly on the node's attrs object.
 */
const NODE_LEVEL_STYLES = ['textAlign'];

/**
 * Style properties that TipTap handles as textStyle marks.
 * These become marks on text nodes.
 */
const TEXT_STYLE_PROPERTIES = ['color', 'fontSize', 'fontFamily'];

/**
 * Maps template style properties to TipTap marks.
 */
interface StyleToMarkMapping {
  property: string;
  toMark: (value: string) => RTEMark | null;
}

const STYLE_TO_MARK_MAPPINGS: StyleToMarkMapping[] = [
  {
    property: 'fontWeight',
    toMark: (value) => {
      if (value === 'bold' || value === '700' || value === 'semibold' || value === '600') {
        return { type: 'bold' };
      }
      return null;
    },
  },
  {
    property: 'fontStyle',
    toMark: (value) => {
      if (value === 'italic') {
        return { type: 'italic' };
      }
      return null;
    },
  },
  {
    property: 'textDecoration',
    toMark: (value) => {
      if (value === 'underline') {
        return { type: 'underline' };
      }
      if (value === 'line-through') {
        return { type: 'strike' };
      }
      return null;
    },
  },
  {
    property: 'highlightColor',
    toMark: (value) => ({
      type: 'textStyle',
      attrs: { backgroundColor: value },
    }),
  },
  {
    property: 'textColor',
    toMark: (value) => ({
      type: 'textStyle',
      attrs: { color: value },
    }),
  },
];

/**
 * Extracted styles from a template block, ready to apply to TipTap nodes.
 */
interface ExtractedBlockStyles {
  /** Styles that apply at the node level (e.g., textAlign) */
  nodeLevelStyles: Record<string, unknown>;
  /** Marks to apply to text nodes (e.g., bold, italic, textStyle) */
  textMarks: RTEMark[];
  /** TextStyle attributes collected for consolidation */
  textStyleAttrs: Record<string, string>;
}

/**
 * Extract style information from a template block.
 * Handles both TipTap-aligned (attrs.style) and legacy (direct props) formats.
 *
 * @param block - The template block to extract styles from
 * @returns Extracted styles categorized by how TipTap handles them
 *
 * @example
 * ```typescript
 * const block = {
 *   id: 'block-1',
 *   type: 'heading',
 *   attrs: { level: 2, style: { textAlign: 'center' } },
 *   props: { fontWeight: 'bold', textColor: '#ff0000' }
 * };
 * const styles = extractBlockStyles(block);
 * // styles.nodeLevelStyles = { textAlign: 'center' }
 * // styles.textMarks = [{ type: 'bold' }, { type: 'textStyle', attrs: { color: '#ff0000' } }]
 * ```
 */
export function extractBlockStyles(block: TemplateBlock): ExtractedBlockStyles {
  const nodeLevelStyles: Record<string, unknown> = {};
  const textMarks: RTEMark[] = [];
  const textStyleAttrs: Record<string, string> = {};

  // Get style from multiple locations (TipTap-aligned, legacy props, flat attrs)
  const style: CSSProperties = block.attrs?.style || {};
  const props = (block.props || {}) as BaseBlockProps;
  // Also check attrs directly (from denormalized block_properties)
  const attrs = (block.attrs || {}) as Record<string, unknown>;

  // DEBUG: Log what we're extracting from this block
  console.log(`[FORMATTING_TRACE] extractBlockStyles - Block ID: ${block.id}, Type: ${block.type}`);
  console.log(`[FORMATTING_TRACE]   attrs:`, block.attrs);
  console.log(`[FORMATTING_TRACE]   attrs.style:`, style);
  console.log(`[FORMATTING_TRACE]   attrs.textAlign (flat):`, attrs.textAlign);
  console.log(`[FORMATTING_TRACE]   attrs.fontWeight (flat):`, attrs.fontWeight);
  console.log(`[FORMATTING_TRACE]   props:`, props);
  console.log(`[FORMATTING_TRACE]   props.textAlign:`, props.textAlign);
  console.log(`[FORMATTING_TRACE]   props.fontWeight:`, props.fontWeight);
  console.log(`[FORMATTING_TRACE]   props.fontStyle:`, props.fontStyle);

  // Merge style sources - check props, then attrs (flat), then attrs.style (nested)
  const mergedStyle: Record<string, string | undefined> = {
    textAlign: props.textAlign || (attrs.textAlign as string) || style.textAlign,
    fontWeight: props.fontWeight || (attrs.fontWeight as string) || style.fontWeight,
    fontStyle: props.fontStyle || (attrs.fontStyle as string),
    textDecoration: props.textDecoration || (attrs.textDecoration as string),
    color: props.textColor || (attrs.color as string) || (attrs.textColor as string) || style.color,
    fontSize: props.fontSize || (attrs.fontSize as string) || style.fontSize,
    backgroundColor: props.backgroundColor || (attrs.backgroundColor as string) || style.backgroundColor,
    highlightColor: props.highlightColor || (attrs.highlightColor as string),
    textColor: props.textColor || (attrs.textColor as string),
  };

  // Process each style property
  for (const [property, value] of Object.entries(mergedStyle)) {
    if (value === undefined || value === null || value === '') continue;
    const stringValue = String(value);

    // 1. Node-level attributes (textAlign)
    if (NODE_LEVEL_STYLES.includes(property)) {
      nodeLevelStyles[property] = stringValue;
      continue;
    }

    // 2. Check for mark mappings (bold, italic, highlight, etc.)
    const mapping = STYLE_TO_MARK_MAPPINGS.find((m) => m.property === property);
    if (mapping) {
      const mark = mapping.toMark(stringValue);
      if (mark) {
        textMarks.push(mark);
      }
      continue;
    }

    // 3. TextStyle mark properties (color, fontSize)
    if (TEXT_STYLE_PROPERTIES.includes(property)) {
      textStyleAttrs[property] = stringValue;
    }
  }

  // Consolidate textStyle marks - merge all textStyle attrs into one mark
  if (Object.keys(textStyleAttrs).length > 0) {
    // Check if we already have a textStyle mark and merge
    const existingTextStyleIndex = textMarks.findIndex((m) => m.type === 'textStyle');
    if (existingTextStyleIndex >= 0) {
      const existingMark = textMarks[existingTextStyleIndex];
      textMarks[existingTextStyleIndex] = {
        ...existingMark,
        attrs: { ...existingMark.attrs, ...textStyleAttrs },
      };
    } else {
      textMarks.push({ type: 'textStyle', attrs: textStyleAttrs });
    }
  }

  // DEBUG: Log extracted styles
  console.log(`[FORMATTING_TRACE]   -> nodeLevelStyles:`, nodeLevelStyles);
  console.log(`[FORMATTING_TRACE]   -> textMarks:`, textMarks);
  console.log(`[FORMATTING_TRACE]   -> textStyleAttrs:`, textStyleAttrs);

  return { nodeLevelStyles, textMarks, textStyleAttrs };
}

/**
 * Build a map of blockId to extracted styles for efficient lookup.
 * Recursively processes nested blocks (e.g., children of container blocks).
 *
 * @param blocks - Array of template blocks from the template
 * @returns Map from blockId to extracted styles
 *
 * @example
 * ```typescript
 * const templateBlocks = [
 *   { id: 'block-1', type: 'heading', props: { textAlign: 'center' } },
 *   { id: 'block-2', type: 'paragraph', props: { fontWeight: 'bold' } },
 * ];
 * const styleMap = buildBlockStyleMap(templateBlocks);
 * // styleMap.get('block-1') => { nodeLevelStyles: { textAlign: 'center' }, ... }
 * ```
 */
export function buildBlockStyleMap(
  blocks: TemplateBlock[]
): Map<string, ExtractedBlockStyles> {
  const map = new Map<string, ExtractedBlockStyles>();

  console.group('[FORMATTING_TRACE] buildBlockStyleMap - Building style map from template blocks');
  console.log('[FORMATTING_TRACE] Input blocks count:', blocks.length);
  console.log('[FORMATTING_TRACE] Input blocks:', JSON.stringify(blocks, null, 2));

  function processBlock(block: TemplateBlock) {
    if (block.id) {
      map.set(block.id, extractBlockStyles(block));
    }
    // Process nested children (e.g., columns, callouts with nested content)
    if (block.children) {
      for (const child of block.children) {
        processBlock(child);
      }
    }
  }

  for (const block of blocks) {
    processBlock(block);
  }

  console.log('[FORMATTING_TRACE] Style map size:', map.size);
  console.log('[FORMATTING_TRACE] Style map entries:');
  map.forEach((styles, blockId) => {
    console.log(`[FORMATTING_TRACE]   ${blockId}:`, styles);
  });
  console.groupEnd();

  return map;
}

/**
 * Merge existing marks with new marks, avoiding duplicates.
 * When marks of the same type exist, their attrs are merged.
 *
 * @param existing - Existing marks on the node
 * @param newMarks - New marks to add
 * @returns Merged array of marks
 */
function mergeMarks(existing: RTEMark[], newMarks: RTEMark[]): RTEMark[] {
  const result = [...existing];

  for (const newMark of newMarks) {
    const existingIndex = result.findIndex((m) => m.type === newMark.type);
    if (existingIndex >= 0) {
      // Merge attrs for same mark type
      result[existingIndex] = {
        ...result[existingIndex],
        attrs: { ...result[existingIndex].attrs, ...newMark.attrs },
      };
    } else {
      result.push(newMark);
    }
  }

  return result;
}

/**
 * Apply styles to a single RTE node recursively.
 * - Node-level styles (textAlign) are applied to the node's attrs
 * - Text marks (bold, color) are applied to text nodes within the content
 *
 * @param node - The RTE node to style
 * @param nodeLevelStyles - Styles to apply at the node level
 * @param textMarks - Marks to apply to text nodes
 * @returns Styled copy of the node
 */
function applyStylesToNode(
  node: RTENode,
  nodeLevelStyles: Record<string, unknown>,
  textMarks: RTEMark[]
): RTENode {
  // Create a shallow copy to avoid mutating the original
  const styledNode = { ...node };

  // Apply node-level styles (like textAlign) to block nodes
  if (Object.keys(nodeLevelStyles).length > 0) {
    styledNode.attrs = { ...styledNode.attrs, ...nodeLevelStyles };
  }

  // Apply text marks to text nodes
  if (node.type === 'text' && textMarks.length > 0) {
    styledNode.marks = mergeMarks(node.marks || [], textMarks);
  }

  // Recursively process children (only pass textMarks, not nodeLevelStyles)
  if (node.content && node.content.length > 0) {
    styledNode.content = node.content.map((child) =>
      applyStylesToNode(child, {}, textMarks)
    );
  }

  return styledNode;
}

/**
 * Transform a single node, applying styles if it has a matching blockId.
 * Recursively transforms all child nodes.
 *
 * @param node - The RTE node to transform
 * @param styleMap - Map of blockId to extracted styles
 * @returns Transformed node with styles applied
 */
function transformNodeWithStyles(
  node: RTENode,
  styleMap: Map<string, ExtractedBlockStyles>
): RTENode {
  let styledNode = { ...node };

  // Check if this node has a blockId with associated styles
  const blockId = node.attrs?.blockId as string | undefined;

  // DEBUG: Log blockId matching
  if (blockId) {
    const hasStyles = styleMap.has(blockId);
    console.log(`[FORMATTING_TRACE] transformNodeWithStyles - Node type: ${node.type}, blockId: ${blockId}, hasMatchingStyles: ${hasStyles}`);
    if (hasStyles) {
      const styles = styleMap.get(blockId)!;
      console.log(`[FORMATTING_TRACE]   MATCH FOUND! Applying styles:`, styles);
      console.log(`[FORMATTING_TRACE]   Node attrs BEFORE:`, node.attrs);
    } else {
      console.log(`[FORMATTING_TRACE]   NO MATCH in styleMap. Available keys:`, Array.from(styleMap.keys()));
    }
  } else {
    console.log(`[FORMATTING_TRACE] transformNodeWithStyles - Node type: ${node.type}, NO blockId attribute. attrs:`, node.attrs);
  }

  if (blockId && styleMap.has(blockId)) {
    const { nodeLevelStyles, textMarks } = styleMap.get(blockId)!;
    styledNode = applyStylesToNode(styledNode, nodeLevelStyles, textMarks);
    console.log(`[FORMATTING_TRACE]   Node attrs AFTER:`, styledNode.attrs);
  }

  // Recursively transform children (even if this node had no blockId)
  if (styledNode.content && styledNode.content.length > 0) {
    styledNode.content = styledNode.content.map((child) =>
      transformNodeWithStyles(child, styleMap)
    );
  }

  return styledNode;
}

/**
 * Main function: Apply template block styles to content_json.
 *
 * This is the primary entry point for style transformation. It takes a TipTap
 * document (content_json) and template blocks, then returns a new document
 * with template styles applied to matching nodes (by blockId).
 *
 * **How it works:**
 * - Backend generates content_json with blockId attribute on each node
 * - This function matches nodes to template blocks by blockId
 * - Template styles (textAlign, fontWeight, color, etc.) are applied
 *
 * @param contentJson - The TipTap document from the backend (must include blockId attrs)
 * @param templateBlocks - Array of template blocks with styling
 * @returns Transformed document with styles applied, or the original if no transformation needed
 *
 * @example
 * ```typescript
 * // In ContentEditorPage.tsx
 * const styledContentJson = useMemo(() => {
 *   if (!contentData?.content_json || !templateData?.builder_blocks) {
 *     return contentData?.content_json;
 *   }
 *   return applyTemplateStylesToContent(
 *     contentData.content_json,
 *     templateData.builder_blocks
 *   );
 * }, [contentData?.content_json, templateData?.builder_blocks]);
 * ```
 */
export function applyTemplateStylesToContent(
  contentJson: RTEDocument | undefined,
  templateBlocks: TemplateBlock[]
): RTEDocument | undefined {
  console.group('[FORMATTING_TRACE] applyTemplateStylesToContent - MAIN ENTRY POINT');
  console.log('[FORMATTING_TRACE] contentJson provided:', !!contentJson);
  console.log('[FORMATTING_TRACE] contentJson.content length:', contentJson?.content?.length || 0);
  console.log('[FORMATTING_TRACE] templateBlocks provided:', !!templateBlocks);
  console.log('[FORMATTING_TRACE] templateBlocks length:', templateBlocks?.length || 0);

  // Return early if no content to transform
  if (!contentJson || !contentJson.content) {
    console.log('[FORMATTING_TRACE] EARLY RETURN: No content to transform');
    console.groupEnd();
    return contentJson;
  }

  // Return early if no template blocks to apply
  if (!templateBlocks || templateBlocks.length === 0) {
    console.log('[FORMATTING_TRACE] EARLY RETURN: No template blocks to apply');
    console.groupEnd();
    return contentJson;
  }

  // Log blockIds in content for matching verification
  console.log('[FORMATTING_TRACE] BlockIds in content_json:');
  contentJson.content.forEach((node, i) => {
    console.log(`[FORMATTING_TRACE]   Node ${i}: type=${node.type}, blockId=${node.attrs?.blockId || 'NONE'}`);
  });

  // Build style lookup map from template blocks
  const styleMap = buildBlockStyleMap(templateBlocks);

  // If no blocks have styles, return original
  if (styleMap.size === 0) {
    console.log('[FORMATTING_TRACE] EARLY RETURN: Style map is empty (no blocks have styles)');
    console.groupEnd();
    return contentJson;
  }

  // Transform content nodes using blockId-based matching
  console.log('[FORMATTING_TRACE] Starting node transformation...');

  // Track match statistics for diagnostics
  let matchedCount = 0;
  let unmatchedCount = 0;
  let noBlockIdCount = 0;
  const unmatchedBlockIds: string[] = [];

  const transformedContent = contentJson.content.map((node) => {
    const blockId = node.attrs?.blockId as string | undefined;
    if (!blockId) {
      noBlockIdCount++;
    } else if (styleMap.has(blockId)) {
      matchedCount++;
    } else {
      unmatchedCount++;
      unmatchedBlockIds.push(blockId);
    }
    return transformNodeWithStyles(node, styleMap);
  });

  // Log comprehensive match statistics
  console.log('[FORMATTING_TRACE] === MATCH STATISTICS ===');
  console.log('[FORMATTING_TRACE] Total nodes in content_json:', contentJson.content.length);
  console.log('[FORMATTING_TRACE] Style map size:', styleMap.size);
  console.log('[FORMATTING_TRACE] Matched (blockId found in styleMap):', matchedCount);
  console.log('[FORMATTING_TRACE] Unmatched (blockId NOT in styleMap):', unmatchedCount);
  console.log('[FORMATTING_TRACE] No blockId attribute:', noBlockIdCount);

  if (unmatchedBlockIds.length > 0) {
    console.log('[FORMATTING_TRACE] Unmatched blockIds:', unmatchedBlockIds);
  }

  if (noBlockIdCount > 0) {
    console.warn('[FORMATTING_TRACE] ⚠️ WARNING: Some nodes have NO blockId - styles cannot be applied!');
  }

  const result = {
    ...contentJson,
    content: transformedContent,
  };

  // Log style application results
  console.log('[FORMATTING_TRACE] === TRANSFORMATION RESULTS ===');

  // Count nodes with styles before and after
  const originalWithStyles = contentJson.content.filter(n =>
    n.attrs?.textAlign || n.attrs?.fontWeight || n.attrs?.color
  ).length;
  const transformedWithStyles = result.content.filter(n =>
    n.attrs?.textAlign || n.attrs?.fontWeight || n.attrs?.color
  ).length;

  console.log('[FORMATTING_TRACE] Nodes with style attrs BEFORE:', originalWithStyles);
  console.log('[FORMATTING_TRACE] Nodes with style attrs AFTER:', transformedWithStyles);
  console.log('[FORMATTING_TRACE] Styles added:', transformedWithStyles - originalWithStyles);

  console.groupEnd();
  return result;
}
